import { useCallback, useEffect, useRef, useState } from 'react';
import { isValidString } from '../common/CommonUtilsMethods';
import type {
    UseWorkyVerificationResendCooldownOptions,
    UseWorkyVerificationResendCooldownResult,
} from './WorkyVerificationResendCooldownInterfaces';
import {
    persistResendCooldownEndEpochMilliseconds,
    readResendCooldownSecondsRemaining,
} from './WorkyVerificationResendCooldownService';

const useWorkyVerificationResendCooldown = ({
    cooldownScope,
    emailAddress,
    startCooldownOnMount = false,
}: UseWorkyVerificationResendCooldownOptions): UseWorkyVerificationResendCooldownResult => {
    const normalizedEmailAddress: string = emailAddress.trim().toLowerCase();
    const hasStartedInitialCooldownRef = useRef<boolean>(false);

    const [resendCooldownSecondsRemaining, setResendCooldownSecondsRemaining] = useState<number>(0);

    const syncCooldownSecondsRemainingFromSession = useCallback((): void => {
        if (!isValidString(normalizedEmailAddress)) {
            setResendCooldownSecondsRemaining(0);
            return;
        }

        const remainingSeconds: number = readResendCooldownSecondsRemaining({
            cooldownScope,
            emailAddress: normalizedEmailAddress,
        });
        setResendCooldownSecondsRemaining(remainingSeconds);
    }, [cooldownScope, normalizedEmailAddress]);

    const beginResendCooldown = useCallback((): void => {
        if (!isValidString(normalizedEmailAddress)) {
            return;
        }

        persistResendCooldownEndEpochMilliseconds({
            cooldownScope,
            emailAddress: normalizedEmailAddress,
        });
        syncCooldownSecondsRemainingFromSession();
    }, [cooldownScope, normalizedEmailAddress, syncCooldownSecondsRemainingFromSession]);

    useEffect(() => {
        syncCooldownSecondsRemainingFromSession();
    }, [syncCooldownSecondsRemainingFromSession]);

    useEffect(() => {
        if (!startCooldownOnMount || !isValidString(normalizedEmailAddress)) {
            return;
        }
        if (hasStartedInitialCooldownRef.current) {
            return;
        }

        hasStartedInitialCooldownRef.current = true;
        beginResendCooldown();
    }, [beginResendCooldown, normalizedEmailAddress, startCooldownOnMount]);

    useEffect(() => {
        if (resendCooldownSecondsRemaining <= 0) {
            return;
        }

        const cooldownIntervalId: ReturnType<typeof setInterval> = setInterval((): void => {
            syncCooldownSecondsRemainingFromSession();
        }, 1000);

        return (): void => {
            clearInterval(cooldownIntervalId);
        };
    }, [resendCooldownSecondsRemaining, syncCooldownSecondsRemainingFromSession]);

    const isResendCooldownActive: boolean = resendCooldownSecondsRemaining > 0;

    const formatResendButtonLabel = useCallback(
        (idleLabel: string): string => {
            if (isResendCooldownActive) {
                return `Resend code in ${resendCooldownSecondsRemaining}s`;
            }
            return idleLabel;
        },
        [isResendCooldownActive, resendCooldownSecondsRemaining]
    );

    return {
        resendCooldownSecondsRemaining,
        isResendCooldownActive,
        beginResendCooldown,
        formatResendButtonLabel,
    };
};

export { useWorkyVerificationResendCooldown };
